<script setup lang="ts">
import { isOrganizationFichePath } from "#shared/utils/listingTypes";
import { CODES_PAYS_SERVIS, zoneServieSchema } from "#shared/utils/paysServis";
import { DONOOR_SOCIAL_URLS } from "@donoor/utils";
import {
  appliquerConsentementMeta,
  HOTES_META_SITE_PUBLIC,
} from "@donoor/utils/meta-pixel";
import { Analytics } from "@vercel/analytics/nuxt";
import { readConsent } from "@donoor/utils/consent";
import ToastsEnDirect from "~/components/shared/ToastsEnDirect.vue";
import { BRAND_BLUE } from "~/utils/brandColors";
import { LANGUES, type Langue } from "~/utils/langue";

const { t } = useI18n();

// og:locale (format Facebook, région_PAYS) et nom anglais de la langue
// (balise `language`, convention historique de ce fichier) pour chaque
// langue proposée (chantier multilingue 2026-10-10).
const OG_LOCALE: Record<Langue, string> = {
  fr: "fr_FR",
  en: "en_GB",
  de: "de_DE",
  es: "es_ES",
  it: "it_IT",
  nl: "nl_NL",
};
const NOM_LANGUE_ANGLAIS: Record<Langue, string> = {
  fr: "French",
  en: "English",
  de: "German",
  es: "Spanish",
  it: "Italian",
  nl: "Dutch",
};

// 🔧 DEBUG MODE: Mettre à true pour afficher les breakpoints Tailwind
const SHOW_DEBUG = false;
// `import.meta.dev` ne peut pas être lu depuis un template Vue (l'expression
// est parsée hors module) : on le fige ici. Vite le remplace par `false` à la
// compilation en production, donc le chunk de TailwindDebug — déjà séparé
// grâce au préfixe `Lazy` — n'y est jamais demandé. (Audit perf 2026-09-14.)
const IS_DEV = import.meta.dev;

const route = useRoute();
// Chemin sans « /en » : toutes les comparaisons de chemin ci-dessous doivent
// reconnaître aussi la version anglaise d'une page (cf. utils/langue.ts).
const cheminFr = useCheminSansLangue();
const { locale } = useI18n();
const localeHtml = computed(() => (LANGUES.includes(locale.value as Langue) ? locale.value : "fr"));

// noindex hors prod : le site n'est indexable QUE sur donoor.org (cf.
// nuxt.config `site.indexable`). Cette balise robots GLOBALE (appliquée à
// chaque page via useHead) primait sur le défaut de nuxt.config → staging
// restait « index, follow » malgré tout. On lit donc le flag runtime du
// module site (@nuxtjs/robots) pour rester cohérent avec robots.txt /
// X-Robots-Tag. Les directives riches (max-image-preview…) ne servent qu'en
// prod ; en noindex elles n'ont pas de sens.
const siteConfig = useSiteConfig();
const globalRobotsContent = computed(() =>
  siteConfig.indexable
    ? "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    : "noindex, nofollow",
);
const googlebotRobotsContent = computed(() =>
  siteConfig.indexable ? "index, follow" : "noindex, nofollow",
);

// Capture UTM + gclid au premier landing (first-touch attribution)
onMounted(() => {
  if (typeof window === "undefined") return;
  const existing = sessionStorage.getItem("donoor_attribution");
  if (!existing) {
    const url = new URL(window.location.href);
    const keys = ["gclid", "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
    const attribution: Record<string, string> = {};
    keys.forEach((k) => {
      const v = url.searchParams.get(k);
      if (v) attribution[k] = v;
    });
    if (Object.keys(attribution).length > 0) {
      sessionStorage.setItem("donoor_attribution", JSON.stringify(attribution));
    }
  }
});

// ─── Consentement cookies → pilotage PostHog (opt-in/opt-out) ─────────────
// Le SDK PostHog est initialisé en `opt_out` par défaut (plugin
// posthog.client.ts) : rien n'est capturé tant que l'utilisateur n'a pas
// accepté les cookies analytics. On (dés)active la capture ici à chaque
// changement du bandeau. Source unique de vérité : `donoor-cookie-consent`.
const { $posthog } = useNuxtApp();
// Mesure d'audience déjà acceptée lors d'une visite précédente ? (lu une fois)
let analyticsDejaAcceptee = import.meta.client ? Boolean(readConsent()?.analytics) : false;
function onConsentUpdate(consent: { analytics: boolean; marketing: boolean }) {
  // ─── Google Consent Mode v2 : signale le choix au tag Google Ads ────────
  // Le maillon manquant : sans ce gtag('consent','update'), le consentement
  // marketing n'était jamais transmis à Google → listes de retargeting vides.
  // marketing → ad_storage/ad_user_data/ad_personalization ; analytics →
  // analytics_storage. Le défaut 'denied' est posé dans nuxt.config (Consent
  // Mode v2). window.gtag n'existe qu'en prod donoor.org (garde-fou typeof).
  if (typeof window !== "undefined" && typeof (window as unknown as { gtag?: unknown }).gtag === "function") {
    (window as unknown as { gtag: (...args: unknown[]) => void }).gtag("consent", "update", {
      ad_storage: consent.marketing ? "granted" : "denied",
      ad_user_data: consent.marketing ? "granted" : "denied",
      ad_personalization: consent.marketing ? "granted" : "denied",
      analytics_storage: consent.analytics ? "granted" : "denied",
    });
    // GA4 « page d'arrivée (not set) » : 69 % des visites venues de Google
    // (audit SEO 2026-10-18). Avant l'accord, GA4 ne reçoit que des signaux
    // anonymes ; à l'accord, il ouvre une NOUVELLE visite dont le premier
    // événement n'est pas une page vue — d'où une visite sans page d'arrivée.
    // On renvoie donc la page en cours, une seule fois, au moment où le
    // visiteur ACCEPTE (pas à chaque chargement d'un accord déjà enregistré).
    if (consent.analytics && !analyticsDejaAcceptee) {
      (window as unknown as { gtag: (...args: unknown[]) => void }).gtag("event", "page_view", {
        send_to: "G-CHDSZ5Q3WB",
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }
  analyticsDejaAcceptee = consent.analytics;

  // ─── Pixel Meta : charge, ou coupe, selon le consentement MARKETING ─────
  // Le pixel n'est jamais téléchargé sans consentement ; s'il tournait déjà et
  // que le visiteur retire son accord, `appliquerConsentementMeta` envoie
  // `fbq('consent','revoke')` — plus rien ne part.
  appliquerConsentementMeta({
    hotesAutorises: HOTES_META_SITE_PUBLIC,
    consentementMarketing: consent.marketing,
  });

  const ph = $posthog;
  if (!ph || !ph.__loaded) return;
  if (consent.analytics) {
    ph.opt_in_capturing();
  } else {
    ph.opt_out_capturing();
  }
}

// Pages où on ne veut pas les schemas globaux (Organization, WebSite, navigation)
const isPublicContentPage = computed(() =>
  cheminFr.value.startsWith("/c/") ||
  cheminFr.value.startsWith("/associations/") ||
  cheminFr.value === "/recherche/cagnottes" ||
  cheminFr.value === "/recherche/associations"
);

/**
 * Pages de paiement / succès / tunnel transactionnel : on cache TOUS les
 * éléments globaux (AppHeader, AppFooter, bouton flottant SupportDonoor)
 * pour offrir une UX minimaliste type "Stripe Checkout" : focus 100% sur
 * le formulaire de paiement, aucune distraction.
 *
 * Concerne :
 *   - /donate/:id           (don via QR code)
 *   - /pay/:slug            (lien de paiement personnalisé)
 *   - /campaigns/donate/:id (don sur cagnotte campagne)
 *   - /c/:slug/payment      (paiement cagnotte type c/)
 *   - /a/:slug              (adhésion — page, paiement et succès)
 *   - /b/:slug              (billetterie — déjà géré pour SupportDonoor)
 *   - /donation-success     (page success don)
 */
/**
 * Widget embarqué (`?embed=1` posé par embed.js) : la page tourne dans l'iframe
 * d'un site tiers. La bannière cookies y est hors de propos — elle couvrait
 * les produits de la vitrine intégrée, et c'est le site hôte qui recueille le
 * consentement de SON visiteur. Sans choix enregistré, la mesure d'audience
 * reste refusée par défaut : masquer la bannière n'active rien.
 */
// Widget : `?embed=1` (tunnels adhésion, billetterie, boutique) OU toute page
// /embed/… (widget de don : embed.js ne pose PAS `embed=1`). Sans le second
// cas, le bandeau cookies s'affichait DANS le widget, sur le site de
// l'association, par-dessus le bouton « Faire un don ».
const isEmbeddedWidget = computed(
  () =>
    route.query.embed === "1" ||
    route.query.embed === "true" ||
    cheminFr.value.startsWith("/embed/"),
);

const isPaymentPage = computed(() => {
  const p = cheminFr.value;
  return (
    // Mode widget embarqué (?embed=1 sur /a, /b…) : jamais de navbar/footer/chat
    // Donoor globaux — la page tourne dans l'iframe d'un site tiers.
    route.query.embed === "1" ||
    p.startsWith("/donate/") ||
    p.startsWith("/pay/") ||
    // Paiement en plusieurs fois : page du payeur (régler une part, changer
    // de carte) — tunnel épuré, la navbar du site couvrait l'échéancier.
    p.startsWith("/echeancier/") ||
    // Widget embarqué (iframe cross-site) : jamais de navbar/footer/chat Donoor.
    p.startsWith("/embed/") ||
    p.startsWith("/embed-preview") ||
    p.startsWith("/campaigns/donate/") ||
    /^\/c\/[^/]+\/payment/.test(p) ||
    // Adhésion : TOUTE la page /a/ (même traitement que /b). La page rend
    // son propre AppHeader — sans cette exclusion, deux en-têtes fixes se
    // superposaient et l'ancien menu (Tap to Pay…) recouvrait le sien
    // (Karim 2026-09-26).
    p.startsWith("/a/") ||
    p.startsWith("/b/") ||
    // Boutique en ligne : même traitement que /b (la page vitrine rend son
    // propre AppHeader ; payment/success = tunnel épuré + embed ?embed=1).
    // Un NOM de boutique après « /boutique/ » : la page produit « /boutique/ »
    // (barre finale ajoutée par le serveur) garde le header et le footer du
    // site — avant le 2026-10-18, startsWith("/boutique/") les lui retirait.
    /^\/boutique\/[^/]+/.test(p) ||
    p === "/donation-success"
  );
});

/**
 * VRAI tunnel de paiement/embed — distinct de `isPaymentPage` ci-dessus, qui
 * mélange deux raisons différentes (cacher l'en-tête EN DOUBLE sur /a, /b,
 * /boutique — ces pages ont le leur ; cacher le chat sur un VRAI tunnel de
 * paiement). `isPaymentPage` masque /a, /b, /boutique en entier pour la
 * première raison ; le chat n'a pas à en pâtir : l'IA aide justement sur ces
 * pages (cagnotte, adhésion, boutique, événement, association —
 * Karim 2026-10-12). Seule l'étape paiement elle-même (saisie carte) le
 * masque, pas la fiche ni la page succès.
 */
const isTunnelPaiementPur = computed(() => {
  const p = cheminFr.value;
  return (
    route.query.embed === "1" ||
    p.startsWith("/donate/") ||
    p.startsWith("/pay/") ||
    p.startsWith("/echeancier/") ||
    p.startsWith("/embed/") ||
    p.startsWith("/embed-preview") ||
    p.startsWith("/campaigns/donate/") ||
    /^\/(c|a|b|boutique)\/[^/]+\/payment/.test(p) ||
    p === "/donation-success"
  );
});

// SEO Meta tags globaux (seront surchargés par les pages spécifiques)
// Canonical dynamique par route (peut être surchargé par useHead/useSeoMeta sur la page)
// Strip query string + trailing slash pour éviter les variantes
const canonicalUrl = computed(() => {
  // Racine SANS trailing slash : le canonical de index.vue est
  // "https://donoor.org" — og:url (dérivé d'ici) doit être identique.
  const path = route.path === "/" ? "" : route.path.replace(/\/$/, "");
  return `https://donoor.org${path}`;
});

useHead({
  // Écrase le `lang: "fr"` statique de nuxt.config (app.head) sur les pages /en.
  htmlAttrs: { lang: localeHtml },
  // Repli traduit (les pages traduites surchargent titre et description).
  title: () => t("navigation.seoGlobal.titre"),
  meta: [
    // La description sera surchargée par index.vue pour la page d'accueil
    // Gardée ici comme fallback pour les autres pages
    {
      name: "description",
      content: computed(() => t("navigation.seoGlobal.description")),
    },
    {
      name: "keywords",
      content:
        "don association, collecte association, QR code don, sadaqa en ligne, reçu fiscal association, plateforme don islamique, don association cultuelle, collecte numérique association",
    },
    // Open Graph
    { property: "og:type", content: "website" },
    {
      property: "og:title",
      content: computed(() => t("navigation.seoGlobal.ogTitre")),
    },
    {
      property: "og:description",
      content: computed(() => t("navigation.seoGlobal.ogDescription")),
    },
    { property: "og:image", content: "https://donoor.org/og-image.png" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    // og:url DYNAMIQUE (audit SEO 2026-07-11) : hardcodé "https://donoor.org"
    // sur TOUTES les pages, Google le prenait comme signal canonique → GSC
    // affichait « canonical déclaré : / » sur /tap-to-pay & co. Doit toujours
    // égaler le canonical de la page (les pages qui surchargent leur canonical
    // surchargent aussi og:url via useSeoMeta).
    { property: "og:url", content: canonicalUrl },
    { property: "og:locale", content: computed(() => OG_LOCALE[locale.value as Langue] ?? "fr_FR") },
    // Twitter
    { name: "twitter:card", content: "summary_large_image" },
    {
      name: "twitter:title",
      content: computed(() => t("navigation.seoGlobal.twitterTitre")),
    },
    {
      name: "twitter:description",
      content: computed(() => t("navigation.seoGlobal.twitterDescription")),
    },
    { name: "twitter:image", content: "https://donoor.org/twitter-card.png" },
    // Autres
    {
      name: "robots",
      content: globalRobotsContent,
    },
    { name: "googlebot", content: googlebotRobotsContent },
    { name: "author", content: "Donoor" },
    { name: "language", content: computed(() => NOM_LANGUE_ANGLAIS[locale.value as Langue] ?? "French") },
  ],
  link: [
    // Canonical dynamique — réactif à la route. La `key` permet aux pages de
    // surcharger ce canonical via useHead({ link: [{ rel: "canonical", key: "canonical", href: ... }] }).
    // Sans cette key, useHead empile les deux <link> au lieu de remplacer.
    { rel: "canonical", href: canonicalUrl, key: "canonical" },
    // Favicon principal pour Google (doit être en premier)
    { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
    { rel: "manifest", href: "/manifest.json" },
    {
      rel: "apple-touch-icon",
      sizes: "180x180",
      href: "/apple-touch-icon.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "32x32",
      href: "/favicon-32x32.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "16x16",
      href: "/favicon-16x16.png",
    },
    // Autodiscovery RSS : déclaré UNE SEULE fois, dans nuxt.config.ts
    // (href /blog/rss.xml). L'ancien doublon ici (/api/rss.xml) créait deux
    // <link rel="alternate"> pour le même feed (audit SEO 2026-07-11).
    // L'endpoint /api/rss.xml reste servi (abonnés existants), mais n'est
    // plus annoncé.
    // Note : hreflang déjà déclaré dans nuxt.config.ts (fr-FR, fr-BE, fr-CH,
    // fr-CA, fr-LU, fr, x-default). Pas de duplication ici.
    // Preconnect pour performance — LCP (Largest Contentful Paint) :
    // - directus : images hero de toutes les pages /c/, /b/, /a/, /associations/
    // - api : appels SSR + données initiales (réduit RTT first byte)
    // NB: preconnect fonts.googleapis/gstatic RETIRÉS — les Google Fonts sont
    // auto-hébergées par @nuxt/fonts (servies en local, aucune requête vers
    // Google), ces 2 connexions tierces étaient donc gaspillées. (Perf 2026-07-31.)
    {
      rel: "preconnect",
      href: "https://directus.donoor.org",
      crossorigin: "anonymous",
    },
    {
      rel: "preconnect",
      href: "https://api.donoor.org",
      crossorigin: "anonymous",
    },
    { rel: "dns-prefetch", href: "https://www.google-analytics.com" },
    // Graphe de connaissances Donoor (GraphRAG) : document JSON-LD @graph
    // reliant toutes les entités (Organization ↔ Services ↔ concepts fiscaux)
    // par @id partagés. Aide les moteurs IA à lire le site comme un réseau de
    // faits, pas des pages isolées. Servi par server/routes/knowledge-graph.jsonld.ts
    {
      rel: "alternate",
      type: "application/ld+json" as const,
      href: "https://donoor.org/knowledge-graph.jsonld",
      title: "Graphe de connaissances Donoor",
    },
  ],
  // ── JSON-LD : UNE SEULE entité par type (audit SEO 2026-07-10). Avant, la
  // home cumulait 3× Organization, 2× SoftwareApplication et 2× WebSite (app.vue
  // + useSchemaOrg dans index.vue) avec des @id/contenus contradictoires →
  // Google ne sait plus quelle entité croire. Désormais : app.vue est LA source
  // unique (Organization #organization + WebSite #website) ; les pages
  // n'ajoutent que leurs schémas SPÉCIFIQUES (FAQPage, Service,
  // BreadcrumbList, Article…).
  //
  // Scoping par type (audit SEO 2026-07-11) :
  //  - Organization + WebSite : GLOBAUX (identité du site sur toutes les pages) ;
  //  - ItemList/SiteNavigationElement : HOMEPAGE SEULEMENT.
  //
  // ⚠️ SoftwareApplication RETIRÉ (audit SemRush 2026-08-05) : Google exige
  // aggregateRating OU review pour ce type → « données structurées invalides »
  // tant que Donoor n'a pas de vrais avis à référencer. Inventer une note =
  // interdit (fake reviews). À REMETTRE avec aggregateRating le jour où des
  // avis réels existent (App Store / Play Store / Trustpilot).
  script: computed(() => isPublicContentPage.value ? [] : [
    {
      type: "application/ld+json" as const,
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": "https://donoor.org/#organization",
        name: "Donoor",
        alternateName: "Donoor — Plateforme de collecte de dons",
        url: "https://donoor.org",
        logo: {
          "@type": "ImageObject",
          url: "https://donoor.org/icons/icon-512x512.png",
          width: 512,
          height: 512,
          caption: "Logo Donoor",
        },
        image: "https://donoor.org/og-image.png",
        description:
          "Plateforme gratuite de collecte de dons pour associations, née en France et ouverte en Belgique, au Luxembourg, aux Pays-Bas, en Allemagne et à Monaco : QR codes, cagnottes en ligne, reçus fiscaux automatiques adaptés à chaque pays, 0 commission.",
        slogan: "Collectez plus de dons pour votre association",
        foundingDate: "2025-09-13",
        // ─── Preuves d'existence — ajoutées le 2026-08-11 ──────────────────
        // Constat du jour : en navigation privée, Google répond « Essayez avec
        // cette orthographe : donor » — il ne croit pas que « donoor » soit un
        // mot. L'entité « Donoor » est déjà prise dans sa base par un village
        // indien, le domaine a moins d'un an, et 4 recherches/jour ne suffisent
        // pas à le convaincre.
        //
        // Un identifiant OFFICIEL VÉRIFIABLE (SIRET) + une adresse postale
        // sont les preuves les plus fortes qu'une entreprise réelle porte ce
        // nom. Ce sont les mêmes informations que les mentions légales — rien
        // de nouveau n'est divulgué, mais ici elles deviennent lisibles par la
        // machine, donc utilisables pour désambiguïser l'entité.
        legalName: "Karim Bouamama",
        founder: { "@type": "Person", name: "Karim Bouamama" },
        identifier: [
          { "@type": "PropertyValue", propertyID: "SIRET", value: "84983617600024" },
          { "@type": "PropertyValue", propertyID: "SIREN", value: "849836176" },
        ],
        address: {
          "@type": "PostalAddress",
          streetAddress: "9 chemin de la Vernique",
          postalCode: "69160",
          addressLocality: "Tassin-la-Demi-Lune",
          addressRegion: "Auvergne-Rhône-Alpes",
          addressCountry: "FR",
        },
        // Pays ouverts aux paiements (source : shared/utils/paysServis.ts).
        areaServed: zoneServieSchema(),
        knowsLanguage: ["fr", "fr-FR"],
        contactPoint: [
          {
            "@type": "ContactPoint",
            contactType: "Service Client",
            email: "contact@donoor.org",
            availableLanguage: ["French"],
            areaServed: [...CODES_PAYS_SERVIS],
          },
          {
            "@type": "ContactPoint",
            contactType: "Sales",
            email: "contact@donoor.org",
            availableLanguage: ["French"],
            areaServed: [...CODES_PAYS_SERVIS],
          },
        ],
        // Réseaux RÉELS de Donoor : LinkedIn, Facebook, Instagram, TikTok.
        // Pas de compte Twitter/X — les anciens liens pointaient vers des
        // comptes inexistants (signal E-E-A-T négatif).
        //
        // C'est par `sameAs` que Google rattache un profil social à
        // l'entreprise. Sans ce rattachement il ne consolide pas l'entité —
        // d'où, en août 2026, un LinkedIn et un Instagram qui sortaient devant
        // donoor.org sur la requête « donoor », et un panneau de connaissance
        // décrivant un village indien homonyme. Un pseudo faux ici coûte donc
        // plus cher qu'un lien mort : il casse l'identification de la marque.
        //
        // Les URL viennent de `DONOOR_SOCIALS` (@donoor/utils) — source unique
        // depuis le 2026-09-10, après six variantes divergentes réparties dans
        // 49 fichiers. Le pseudo Facebook porte un ZÉRO (`donoor.0rg`) ; ne pas
        // le « corriger ». Instagram, lui, utilise `donoor_org` depuis le
        // 2026-10-25. TikTok utilise la lettre « o ».
        sameAs: [
          "https://www.linkedin.com/company/donoor",
          ...DONOOR_SOCIAL_URLS,
        ],
      }),
    },
    {
      type: "application/ld+json" as const,
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": "https://donoor.org/#website",
        name: "Donoor",
        url: "https://donoor.org",
        potentialAction: {
          "@type": "SearchAction",
          // Cible = l'OUTIL de recherche, pas un rayon. Depuis la refonte du
          // 2026-08-06, `/associations?q=` est une vue filtrée en noindex :
          // annoncer une page non indexable comme cible de la searchbox Google
          // serait incohérent. `/recherche` est fait pour ça.
          target: "https://donoor.org/recherche?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
      }),
    },
    ...(cheminFr.value !== "/" ? [] : [
    {
      type: "application/ld+json" as const,
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ItemList",
        "@id": "https://donoor.org/#sitenavigation",
        name: "Navigation principale Donoor",
        itemListElement: [
          {
            "@type": "SiteNavigationElement",
            position: 1,
            name: t("navigation.appHeader.connexion"),
            description: "Connectez-vous à votre espace Donoor",
            url: "https://dashboard.donoor.org/login",
          },
          {
            "@type": "SiteNavigationElement",
            position: 2,
            name: "Cagnottes de Collecte Association",
            description: "Créez des cagnottes pour vos projets : travaux, événements, urgences",
            url: "https://donoor.org/recherche/cagnottes",
          },
          {
            "@type": "SiteNavigationElement",
            position: 3,
            name: "QR Code Don Association",
            description: "Créez des QR codes gratuits et illimités pour vos collectes",
            url: "https://donoor.org/qr-codes",
          },
          {
            "@type": "SiteNavigationElement",
            position: 4,
            name: "Liens de Paiement",
            description: "Partagez des liens de paiement par email, SMS ou réseaux sociaux",
            url: "https://donoor.org/lien-de-paiement",
          },
          {
            "@type": "SiteNavigationElement",
            position: 5,
            name: t("association.page.breadcrumbAssociations"),
            description: "Découvrez les associations partenaires et faites un don",
            url: "https://donoor.org/recherche/associations",
          },
        ],
      }),
    },
    ]),
  ]),
});
</script>

<template>
  <UApp>
    <!-- Barre de chargement fine en haut, déclenchée sur chaque navigation -->
    <NuxtLoadingIndicator :color="BRAND_BLUE" :height="3" :throttle="0" :duration="3000" />

    <!-- Fond coloré diagonal (mobile uniquement, page d'accueil seulement) — désactivé -->
    <!-- <div
      v-if="cheminFr === '/'"
      class="md:hidden absolute top-0 left-0 right-0 h-[450px] -z-50 overflow-hidden pointer-events-none"
    >
      <div
        class="absolute inset-0 bg-gradient-diagonal animate-gradient-shift"
      />
    </div> -->

    <AppHeader
      v-if="!['login', 'register'].includes(String(route.name)) && !cheminFr.startsWith('/c/') && !isPaymentPage"
      :links="navLinks"
      :couleur-page="isOrganizationFichePath(cheminFr)"
    />

    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>

    <!-- Footer 100% sous le pli : hydration retardée jusqu'à ce qu'il entre dans
         le viewport (Nuxt hydrate-on-visible). Le HTML SSR reste rendu immédiatement
         (aucun impact SEO/CLS), seul le JS d'hydratation (gros arbre de nav + useAnalytics)
         quitte le chemin critique du boot → gain TBT. (Audit perf TBT 2026-07-31.) -->
    <!-- Fiches assos : footer ALLÉGÉ (pattern HelloAsso — conversion + moins de
         boilerplate SEO). Le footer complet reste partout ailleurs. Karim 2026-08-05. -->
    <LazyVisibleFooterLite
      v-if="isOrganizationFichePath(cheminFr) && !isPaymentPage"
      hydrate-on-visible
    />
    <LazyVisibleAppFooter
      v-if="!['login', 'register'].includes(String(route.name)) && !cheminFr.startsWith('/c/') && !cheminFr.startsWith('/b/') && !cheminFr.startsWith('/a/') && !isOrganizationFichePath(cheminFr) && cheminFr !== '/erreur' && !isPaymentPage"
      hydrate-on-visible
    />

    <!-- Chat support visiteur (remplace l'ancien bouton SupportDonoor) —
         masqué sur les tunnels de paiement/checkout, login/register.
         Client-only + composable chargé au montage → aucun impact SSR/LCP.
         Lazy + hydrate-on-idle (audit perf 2026-09-14) : le composant fait 951
         lignes et se retrouvait dans le chunk d'ENTRÉE, donc téléchargé et
         évalué au boot de CHAQUE page (entrée mesurée à 285 Ko gzip / 489 ms).
         Il est déjà <ClientOnly> : rien n'est rendu côté serveur, la bulle
         n'apparaissait donc de toute façon qu'après hydratation. On la sort du
         chemin critique — aucune différence perceptible, le chunk part à
         l'idle. -->
    <LazyIdlePublicChatWidget
      v-if="!['login', 'register'].includes(String(route.name)) && !isTunnelPaiementPur"
      hydrate-on-idle
    />

    <!-- Modal Calendly GLOBAL (prise de RDV) — ouvert par les CalendlyButton
         du header/footer/contact via useCalendlyModal. Karim 2026-08-05.
         Lazy + hydrate-on-idle (audit perf 2026-09-14) : UModal + son habillage
         voyageaient dans le chunk d'entrée alors que la quasi-totalité des
         visiteurs ne prennent jamais rendez-vous. L'état d'ouverture vit dans
         le composable (useCalendlyModal), pas ici : le modal reste donc
         pilotable depuis n'importe quel bouton, exactement comme avant. -->
    <LazyIdleCalendlyModal hydrate-on-idle />

    <!-- <WhatsAppFloating v-if="!['login', 'register'].includes(String(route.name))" /> -->

    <!-- Promo Modal (apparaît après 5s) -->
    <!-- <PromoModal /> -->

    <!-- Debug Tailwind breakpoints — DÉVELOPPEMENT UNIQUEMENT.
         `import.meta.dev` est remplacé par `false` à la compilation en prod :
         la branche est éliminée et le chunk n'est jamais demandé. Avant
         (audit perf 2026-09-14), le composant partait en prod dans le chunk
         d'entrée pour n'y afficher jamais rien (SHOW_DEBUG = false). -->
    <LazyTailwindDebug v-if="IS_DEV" :show="SHOW_DEBUG" />

    <!-- Vercel Analytics : pure télémétrie, aucun besoin d'être prêt au boot.
         ClientOnly → hors du chunk d'entrée et hydraté après le rendu. -->
    <ClientOnly>
      <Analytics />
    </ClientOnly>

    <!-- Toasts « en direct » (quelqu'un vient de donner…), en bas à gauche -->
    <ClientOnly>
      <ToastsEnDirect />
    </ClientOnly>

    <!-- Bannière consentement cookies (RGPD CNIL). Affichée tant que
         l'utilisateur n'a pas fait de choix. Le composant gère lui-même
         son state via localStorage. hydrate-on-idle : le HTML SSR s'affiche
         immédiatement (pas de régression visuelle/CLS ni RGPD), seul le JS
         attend l'idle → hors chemin critique boot. (Audit perf TBT 2026-07-31.) -->
    <LazyIdleCookieConsentBanner
      v-if="!isEmbeddedWidget"
      hydrate-on-idle
      @update:consent="onConsentUpdate"
    />

    <!-- Sandbox (faux paiements) : pastille visible sur ses seules adresses. -->
    <PastilleEnvironnementTest />
  </UApp>
</template>

<style scoped>
.bg-gradient-diagonal {
  background: linear-gradient(
    135deg,
    rgba(16, 185, 129, 0.15) 0%,
    rgba(59, 130, 246, 0.1) 100%
  );
  clip-path: polygon(0 0, 100% 0, 100% 62%, 0 80%);
}

@keyframes gradient-shift {
  0%,
  100% {
    background: linear-gradient(
      135deg,
      rgba(16, 185, 129, 0.15) 0%,
      rgba(59, 130, 246, 0.1) 100%
    );
  }
  33% {
    background: linear-gradient(
      135deg,
      rgba(52, 211, 153, 0.15) 0%,
      rgba(16, 185, 129, 0.1) 100%
    );
  }
  66% {
    background: linear-gradient(
      135deg,
      rgba(59, 130, 246, 0.15) 0%,
      rgba(52, 211, 153, 0.1) 100%
    );
  }
}

.animate-gradient-shift {
  animation: gradient-shift 12s ease-in-out infinite;
}
</style>
